import { Grid } from "@mui/material";
import React from "react";
import { AppUtils } from "source/framework/appUtils";
import { ISessionState } from "source/models/session/sessionState";
import { IUnitNodePermissions } from "source/models/unit/unitNodePermissions";
import { Translations } from "ui/Scripts/source/models/translations";
import * as instantMessageService from "../../../models/services/instantMessageService";
import { Dialog } from "../../framework/Dialog/Dialog";
import { GridRow } from "../../framework/Grid/GridRow";
import { IPageNavMenuItem, PageNavMenuItems } from "../../framework/PageNav/PageNavMenuItems";
import { InstantMessagePartnersSettings } from "../InstantMessagePartnersSettings";
import { UnitPermissionSettingsNew } from "source/components/unit/Unit/UnitPermissionSettingsNew";

/* eslint-disable no-unused-vars */
export const enum InstantMessageSettingsDialogMenuItem {
    Permissions = "1",
    InstantMessagePartners = "2",
}
/* eslint-enable no-unused-vars */

export interface IInstantMessageSettingsDialogProp {
    sessionState: ISessionState;
    companyId: string;
    settingsActions: IPageNavMenuItem[];
    onClose: (refreshView: boolean) => void;
}

export interface IInstantMessageSettingsDialogState {
    unitNodePermissions: IUnitNodePermissions;
    activeMenuItem: InstantMessageSettingsDialogMenuItem;
}

export class InstantMessageSettingsDialog extends React.Component<IInstantMessageSettingsDialogProp, IInstantMessageSettingsDialogState> {
    private permissionsRef = React.createRef<UnitPermissionSettingsNew>();
    private refreshViewOnClose = false;

    constructor(props: IInstantMessageSettingsDialogProp) {
        super(props);
        this.state = {
            unitNodePermissions: null,
            activeMenuItem: InstantMessageSettingsDialogMenuItem.Permissions,
        };
    }

    async componentDidMount(): Promise<void> {
        await this.loadPermissions();
    }

    // #region Permissions
    onUnitPermissionSettingsCancel = async (): Promise<boolean> => {
        return await this.permissionsRef.current.handleCancel(false);
    };

    loadPermissions = async () => {
        const result = await AppUtils.callService(() => instantMessageService.getInstantMessagePermissions(this.props.companyId), false);
        if (!result) return;
        this.setState({
            unitNodePermissions: result.unitNodePermissions
        });
    };

    handleUnitPermissionSettingsSave = async (obj: any) => {
        const result = await instantMessageService.saveUnitNodePermissions(obj);
        this.loadPermissions();
        return result;
    };
    // #endregion Permissions

    handleToggleActiveMenuItem = async (menuItem: InstantMessageSettingsDialogMenuItem) => {
        const state = this.state;
        if (state.activeMenuItem === menuItem) return;
        //Check current tab cancel
        if (state.activeMenuItem === InstantMessageSettingsDialogMenuItem.Permissions) {
            if (!await this.onUnitPermissionSettingsCancel()) return;
        }
        //Load new tab data
        if (menuItem === InstantMessageSettingsDialogMenuItem.Permissions) {
            await this.loadPermissions();
        }
        //Set new tab active
        this.setState({
            activeMenuItem: menuItem
        });
    };

    handleSubViewOk = (_closeDialog: boolean) => {
        this.refreshViewOnClose = true;
    };

    handleCancel = async (closeDialog: boolean) => {
        if (!closeDialog) return;
        this.props.onClose(this.refreshViewOnClose);
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <Dialog
                id="instant-message-settings-dialog"
                maxWidth="lg"
                maxHeight="100%"
                title={Translations.Settings}
                body={
                    <GridRow flexGrow={2}>
                        <Grid item sm={3}>
                            <PageNavMenuItems
                                activeId={state.activeMenuItem}
                                items={props.settingsActions}
                                onToggleMenuItem={this.handleToggleActiveMenuItem}
                            />
                        </Grid>
                        <Grid item sm={9} display="flex" flexDirection="column" flexGrow={2}>
                            {state.activeMenuItem === InstantMessageSettingsDialogMenuItem.Permissions && state.unitNodePermissions &&
                                <UnitPermissionSettingsNew
                                    ref={this.permissionsRef}
                                    infoText={Translations.InstantMessageModuleInfoPermissions}
                                    companyId={props.companyId}
                                    unitNodePermissions={state.unitNodePermissions}
                                    cardView={true}
                                    onSave={this.handleUnitPermissionSettingsSave}
                                    onOk={this.handleSubViewOk}
                                    onCancel={this.handleCancel}
                                />
                            }
                            {state.activeMenuItem === InstantMessageSettingsDialogMenuItem.InstantMessagePartners &&
                                <InstantMessagePartnersSettings
                                    companyId={props.companyId}
                                    onCancel={this.handleCancel}
                                />
                            }
                        </Grid>
                    </GridRow>
                }
                buttons={
                    state.activeMenuItem !== InstantMessageSettingsDialogMenuItem.Permissions
                        ? [{ title: Translations.Close, variant: "outlined", onClick: () => this.handleCancel(true) }]
                        : null
                }
                onClose={() => this.handleCancel(true)}/>
        );
    }
}
